import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatINR, productImg } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { Minus, Plus, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/track";

const Star = ({ filled = true }) => <span style={{ color: filled ? "var(--bloom)" : "#F0C9D6" }}>★</span>;
const Stars5 = ({ rating = 5 }) => (
  <span className="rating-stars">{[1,2,3,4,5].map((i) => <Star key={i} filled={i <= Math.round(rating)} />)}</span>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const [related, setRelated] = useState([]);

  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [open, setOpen] = useState({ details: true, apply: false, ship: false });

  const loadReviews = useCallback(async (s) => {
    const { data } = await api.get(`/products/${s}/reviews`);
    setReviews(data.reviews);
    setAvgRating(data.avg_rating);
    setReviewCount(data.review_count);
  }, []);

  const loadProduct = useCallback(async () => {
    try {
      const { data } = await api.get(`/products/${slug}`);
      setProduct(data);
      setActiveImg(0);
      setAvgRating(data.avg_rating || 0);
      setReviewCount(data.review_count || 0);
      const { data: all } = await api.get("/products");
      setRelated(all.filter((p) => p.id !== data.id).slice(0, 4));
      // Fire ViewContent (Meta Pixel) + view_item (GA4)
      track("ViewContent", {
        product_id: data.id, name: data.name,
        category: data.category, price: data.price,
      });
    } catch { navigate("/shop"); }
  }, [slug, navigate]);

  useEffect(() => {
    loadProduct();
    loadReviews(slug);
  }, [slug, loadProduct, loadReviews]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate(`/login?next=/product/${slug}`); return; }
    if (!reviewForm.comment.trim()) { toast.error("Add a comment"); return; }
    setSubmittingReview(true);
    try {
      await api.post(`/products/${slug}/reviews`, reviewForm);
      toast.success("Review posted ♡");
      setReviewForm({ rating: 5, comment: "" });
      await loadReviews(slug);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post");
    } finally { setSubmittingReview(false); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete your review?")) return;
    await api.delete(`/reviews/${id}`); toast.success("Deleted"); await loadReviews(slug);
  };

  if (!product) return <div className="min-h-screen p-12"><div className="surface h-96 animate-pulse" /></div>;
  const userReview = reviews.find((r) => r.user_id === user?.id);
  const ratingBuckets = [5,4,3,2,1].map((n) => {
    const c = reviews.filter((r) => r.rating === n).length;
    return { n, c, pc: reviews.length ? Math.round((c/reviews.length)*100) : 0 };
  });

  return (
    <div data-testid="product-page" className="pb-24">
      {/* Crumb */}
      <div className="max-w-[1180px] mx-auto px-6 py-5 text-[.85rem] text-[var(--ink-soft)] flex gap-2">
        <Link to="/" className="hover:text-[var(--ink)]">Home</Link><span>/</span>
        <Link to="/shop" className="hover:text-[var(--ink)]">Nail stickers</Link><span>/</span>
        <span className="text-[var(--ink)]">{product.name}</span>
      </div>

      {/* PDP */}
      <main className="max-w-[1180px] mx-auto px-6 grid lg:grid-cols-[1.05fr_.95fr] gap-12">
        {/* Gallery */}
        <div>
          <div className="relative rounded-[36px] aspect-square overflow-hidden grid place-items-center" style={{ background: "radial-gradient(circle at 30% 30%, #fff 0%, var(--rose) 60%, var(--petal-2) 100%)" }}>
            <div className="float-orn" style={{ top: "8%", left: "8%", transform: "scale(.7)" }}><div className="petal-shape" /></div>
            <div className="float-orn" style={{ bottom: "12%", right: "10%", animationDelay: "1.4s", transform: "scale(.55)" }}><div className="petal-shape" /></div>
            <div className="float-orn" style={{ top: "18%", right: "16%", animationDelay: ".7s", fontSize: "1.6rem" }}>✦</div>
            <img src={productImg(product.images?.[activeImg])} alt={product.name} className="w-[78%] h-[78%] object-cover rounded-3xl shadow-[0_30px_60px_-25px_rgba(224,53,107,.45)]" />
          </div>
          {product.images?.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {product.images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`thumb-${i}`}
                  className={`aspect-square rounded-2xl overflow-hidden border-2 transition ${activeImg === i ? "" : "opacity-65 hover:opacity-100"}`}
                  style={{ borderColor: activeImg === i ? "var(--bloom)" : "transparent", background: "#fff" }}
                >
                  <img src={productImg(src)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <span className="eyebrow">Gel nail stickers</span>
          <h1 className="text-[clamp(2rem,4vw,3rem)] mt-3 mb-3">{product.name}</h1>
          <div className="flex items-center gap-3 mb-5">
            <Stars5 rating={avgRating || 5} />
            <b className="font-bold">{avgRating || 5}</b>
            <span className="text-[var(--ink-soft)] text-[.92rem]">· {reviewCount} reviews</span>
          </div>
          <div className="flex items-baseline gap-3 mb-1">
            <span className="font-serif text-[2rem] font-semibold">{formatINR(product.price)}</span>
            {product.compare_at && <span className="text-lg text-[var(--ink-soft)] line-through">{formatINR(product.compare_at)}</span>}
            {product.compare_at && (
              <span className="tag-pill">Save {Math.round((1 - product.price / product.compare_at) * 100)}%</span>
            )}
          </div>
          <p className="text-[.85rem] text-[var(--ink-soft)] mb-6">Inclusive of all taxes · Free shipping over ₹699</p>
          <p className="text-[var(--ink)] leading-relaxed mb-7">{product.description}</p>

          {product.stock <= 5 && product.stock > 0 && (
            <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider" style={{ background: "var(--gold)", color: "var(--ink)" }}>
              Only {product.stock} left
            </div>
          )}

          <div className="flex items-center gap-4 mb-3">
            <div className="flex items-center bg-white rounded-full p-1 border border-[#fde0e8]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-dec" className="w-10 h-10 rounded-full hover:bg-[var(--petal)] grid place-items-center"><Minus className="w-4 h-4" /></button>
              <span data-testid="qty-value" className="w-10 text-center font-bold">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="qty-inc" className="w-10 h-10 rounded-full hover:bg-[var(--petal)] grid place-items-center"><Plus className="w-4 h-4" /></button>
            </div>
            <button
              onClick={() => {
                addItem(product, qty);
                track("AddToCart", {
                  product_id: product.id, name: product.name,
                  category: product.category, price: product.price, qty,
                });
              }}
              disabled={product.stock === 0}
              data-testid="add-to-cart-button"
              className="btn btn-primary flex-1 justify-center"
            >
              {product.stock === 0 ? "Sold out" : <>Add to bag · {formatINR(product.price * qty)}</>}
            </button>
          </div>
          <button onClick={() => {
            addItem(product, qty);
            track("AddToCart", {
              product_id: product.id, name: product.name,
              category: product.category, price: product.price, qty,
            });
            navigate("/checkout");
          }} data-testid="buy-now-button" className="btn btn-dark w-full justify-center mt-2">Buy it now</button>

          <div className="grid grid-cols-2 gap-3 mt-7">
            {(product.perks || [
              { icon: "☀️", title: "No UV lamp" },
              { icon: "💧", title: "Waterproof 12–15 days" },
              { icon: "🌿", title: "Vegan & cruelty-free" },
              { icon: "🫧", title: "No glue, no damage" },
            ]).map((p) => (
              <div key={p.title} className="flex items-center gap-2.5 surface px-3 py-2.5 text-[.92rem]">
                <span className="text-lg">{p.icon}</span>{p.title}
              </div>
            ))}
          </div>

          <div className="mt-7 space-y-2">
            {[
              { id: "details", title: "Details", body: product.description },
              { id: "apply",   title: "How to apply", body: "1. Wipe each nail with the alcohol pad. 2. Choose your size — if in between, size down. 3. Set the sticker above the cuticle, press from centre out. 4. Fold excess over the tip and file downward, one direction." },
              { id: "ship",    title: "Shipping & returns", body: "Dispatched within 24 hours. Free shipping on orders over ₹699. Delivery 3–6 working days across India. All sales are final — please note we do not accept returns or exchanges. If your parcel arrives damaged or you receive the wrong item, reach out to us via DM on Instagram within 48 hours and we'll make it right." },
            ].map((a) => (
              <div key={a.id} className="surface px-5">
                <button onClick={() => setOpen({ ...open, [a.id]: !open[a.id] })} data-testid={`accordion-${a.id}`} className="w-full flex items-center justify-between py-4 text-left font-semibold">
                  {a.title}
                  <span style={{ color: "var(--bloom)" }}>{open[a.id] ? "−" : "+"}</span>
                </button>
                {open[a.id] && <div className="pb-4 text-[var(--ink-soft)] text-[.93rem] leading-relaxed">{a.body}</div>}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* REVIEWS */}
      <section data-testid="reviews-section" className="mt-24 py-20" style={{ background: "var(--petal-2)" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center max-w-[640px] mx-auto mb-12">
            <span className="eyebrow">{reviewCount}+ happy nails</span>
            <h2 className="text-[clamp(2rem,4vw,2.8rem)] mt-3 mb-3">What people are saying</h2>
          </div>

          <div className="grid md:grid-cols-[280px_1fr] gap-10 mb-10">
            <div className="surface p-7 text-center">
              <div className="font-serif text-[3.5rem] font-semibold leading-none">{avgRating || "—"}</div>
              <Stars5 rating={avgRating || 5} />
              <p className="mt-2 text-[var(--ink-soft)] text-sm">Based on {reviewCount} reviews</p>
            </div>
            <div className="space-y-2">
              {ratingBuckets.map((b) => (
                <div key={b.n} className="flex items-center gap-3">
                  <span className="w-8 text-[var(--ink-soft)] text-sm">{b.n}★</span>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "rgba(237,106,146,.18)" }}>
                    <div className="h-full rounded-full" style={{ width: `${b.pc}%`, background: "linear-gradient(90deg, var(--bloom), var(--punch))" }} />
                  </div>
                  <span className="w-10 text-right text-sm text-[var(--ink-soft)]">{b.pc}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Write review */}
          <div className="mb-8">
            {user ? (
              userReview ? (
                <div data-testid="your-review" className="surface p-5 flex items-start gap-4">
                  <div className="flex-1">
                    <div className="eyebrow text-[.65rem]">Your review</div>
                    <Stars5 rating={userReview.rating} />
                    <p className="mt-2 text-[var(--ink-soft)]">{userReview.comment}</p>
                  </div>
                  <button onClick={() => deleteReview(userReview.id)} data-testid="delete-my-review" className="text-[var(--bloom)] hover:text-[var(--punch)]"><Trash2 className="w-4 h-4" /></button>
                </div>
              ) : (
                <form onSubmit={submitReview} className="surface p-6">
                  <div className="eyebrow">Write a review</div>
                  <div className="mt-3 flex items-center gap-2">
                    {[1,2,3,4,5].map((r) => (
                      <button type="button" key={r} onClick={() => setReviewForm({ ...reviewForm, rating: r })} data-testid={`rate-${r}`}
                        className="text-2xl transition hover:scale-110" style={{ color: reviewForm.rating >= r ? "var(--bloom)" : "#F0C9D6" }}>★</button>
                    ))}
                    <span className="ml-2 text-xs text-[var(--ink-soft)]">{reviewForm.rating}/5</span>
                  </div>
                  <textarea
                    data-testid="review-comment"
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    placeholder="Spill the tea about these stickers..."
                    rows={3}
                    className="w-full mt-3 bg-white border border-[#fde0e8] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--bloom)] resize-none"
                  />
                  <button type="submit" disabled={submittingReview} data-testid="submit-review" className="btn btn-primary mt-3 text-sm">
                    {submittingReview ? "Posting…" : "Post review"}
                  </button>
                </form>
              )
            ) : (
              <div className="surface p-5 text-center text-[var(--ink-soft)]">
                <Link to={`/login?next=/product/${slug}`} className="font-bold" style={{ color: "var(--bloom)" }}>Sign in</Link> to leave a review.
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {reviews.length === 0 ? (
              <p className="md:col-span-3 text-center text-[var(--ink-soft)]">No reviews yet. Be the first.</p>
            ) : reviews.map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="surface p-6">
                <Stars5 rating={r.rating} />
                <p className="mt-3 text-[var(--ink)] leading-relaxed text-[.95rem]">{r.comment}</p>
                <div className="flex items-center gap-3 mt-4">
                  {r.user_picture ? (
                    <img src={r.user_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full grid place-items-center text-white font-bold" style={{ background: "linear-gradient(135deg, var(--bloom), var(--punch))" }}>
                      {r.user_name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <div>
                    <b>{r.user_name}</b><br />
                    <small className="text-[var(--ink-soft)] text-xs">{r.verified ? "Verified · " : ""}{new Date(r.created_at).toLocaleDateString()}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-20">
          <div className="max-w-[1180px] mx-auto px-6">
            <div className="mb-8">
              <span className="eyebrow">More to love</span>
              <h2 className="text-[2rem] mt-2">You may also like</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {related.map((p) => (
                <Link key={p.id} to={`/product/${p.slug}`} data-testid={`related-${p.slug}`} className="product-card surface block p-4">
                  <div className="aspect-square rounded-2xl overflow-hidden mb-3" style={{ background: "linear-gradient(135deg, var(--rose), #fff)" }}>
                    <img src={productImg(p.images?.[0])} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <h4 className="font-serif font-semibold">{p.name}</h4>
                  <div className="font-bold mt-1" style={{ color: "var(--ink)" }}>{formatINR(p.price)}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
